import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { fabForLocation, type FabAction } from "@/components/layout/fabConfig";
import { runFabIntent } from "@/components/layout/fabIntents";
import { fabPositionClass, fabTriggerClass } from "@/components/shared/Fab";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { OFFLINE_MSG } from "@/lib/offline";
import { cn } from "@/lib/utils";

function runAction(
  action: FabAction,
  navigate: (to: string) => void,
  online: boolean,
  pathname: string,
  search: string
) {
  if (!online) {
    toast.error(OFFLINE_MSG);
    return;
  }
  if (action.intent && runFabIntent(action.intent)) return;
  if (!action.to) return;
  navigate(typeof action.to === "function" ? action.to(pathname, search) : action.to);
}

/**
 * The only mobile FAB. Hidden from 768px up, where the page header keeps the button.
 */
export function RouteFab() {
  const location = useLocation();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const route = fabForLocation(location.pathname, location.search);
  const [open, setOpen] = React.useState(false);
  const multi = (route?.actions.length ?? 0) > 1;

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!route || route.actions.length === 0) return null;

  const triggerLabel = multi ? route.label : (route.actions[0]?.label ?? route.label);

  return (
    <>
      {open && multi ? (
        <button
          type="button"
          aria-label="Gyors műveletek bezárása"
          className="fixed inset-0 z-[44] bg-slate-950/30 md:hidden print:hidden"
          onClick={() => setOpen(false)}
        />
      ) : null}
      <div className={cn(fabPositionClass, "md:hidden")} data-testid="fab-anchor">
        {open && multi ? (
          <ul
            data-testid="fab-speed-dial"
            role="menu"
            aria-label={route.label}
            className="mb-3 flex flex-col items-stretch gap-2"
          >
            {route.actions.map((action) => (
              <li key={action.id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  data-testid={`fab-action-${action.id}`}
                  className="flex min-h-11 w-full items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold text-slate-950 shadow-lg dark:bg-slate-950 dark:text-slate-50"
                  onClick={() => {
                    setOpen(false);
                    runAction(action, navigate, online, location.pathname, location.search);
                  }}
                >
                  <action.Icon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
                  {action.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <button
          type="button"
          data-testid="fab"
          aria-label={triggerLabel}
          aria-expanded={multi ? open : undefined}
          aria-haspopup={multi ? "menu" : undefined}
          className={fabTriggerClass}
          onClick={() => {
            if (!multi) {
              const action = route.actions[0];
              if (action) runAction(action, navigate, online, location.pathname, location.search);
              return;
            }
            setOpen((v) => !v);
          }}
        >
          {open && multi ? (
            <X className="h-6 w-6" aria-hidden />
          ) : multi ? (
            <Plus className="h-6 w-6" aria-hidden />
          ) : (
            (() => {
              const Icon = route.actions[0]?.Icon ?? Plus;
              return <Icon className="h-6 w-6" aria-hidden />;
            })()
          )}
        </button>
      </div>
    </>
  );
}
